import {Component, EventEmitter, input, Input, OnInit, Output} from "@angular/core"
import {FlexModule} from "@angular/flex-layout";
import {FormControl, Validators} from "@angular/forms";
import {MatStepperModule} from "@angular/material/stepper"
import {
  BookingConfig,
  PatternType,
  Space,
  SpaceAvailability,
  SpacePricingRateCategory
} from "@features/leasing/lease-bookings/bookings.model";
import {CalendarViewComponent} from "@features/leasing/lease-bookings/calendar-view/calendar-view.component";
import {LeaseBookingsService} from "@features/leasing/lease-bookings/lease-bookings.service";
import {
  BookingDatesAndSpacesComponent
} from "@features/leasing/lease-bookings/manage-booking/booking-dates-and-spaces/booking-dates-and-spaces.component";
import {
  BookingRatesAndChargesComponent
} from "@features/leasing/lease-bookings/manage-booking/booking-rates-and-charges/booking-rates-and-charges.component";
import {
  BookingReviewContractComponent
} from "@features/leasing/lease-bookings/manage-booking/booking-review-contract/booking-review-contract.component";
import {
  BookingTenantInfoComponent
} from "@features/leasing/lease-bookings/manage-booking/booking-tenant-info/booking-tenant-info.component";
import {
  LeaseGridSpace,
  LeasePricingPeriod,
  LeaseSummary, OccupancyType,
  OccupiedSpace,
  RentalPricingOption
} from "@features/leasing/leasing.model";
import {SelectableCategory} from "@features/settings/industries/industry.model";
import {ManageIndustriesService} from "@features/settings/industries/manage-industries.service";
import {RetailerLookupComponentOptions} from "@shared-components/retailer-lookup/retailer-lookup.model";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {DateConfig} from "@ui-kit/webkit/components/booking-calendar/booking-calendar.model";
import {concatMap, forkJoin, map, tap} from "rxjs";

@Component({
  selector: 'app-manage-booking',
  standalone: true,
  imports: [
    MatStepperModule,
    FlexModule,
    BookingTenantInfoComponent,
    BookingDatesAndSpacesComponent,
    BookingReviewContractComponent,
    BookingRatesAndChargesComponent,
    CalendarViewComponent
  ],
  templateUrl: './manage-booking.component.html',
  styleUrl: './manage-booking.component.scss'
})
export class ManageBookingComponent implements OnInit {
  @Input() offering!: any;
  @Input() editType: 'new' | 'add' | 'edit' = 'new';
  @Input() retailerId?: number;
  @Input() lease?: LeaseSummary;
  @Input() pricingPeriod?: LeasePricingPeriod;

  /**
   * Options for the embedded retailer lookup component.
   * The options can be partially specified, allowing flexibility in customizing
   * the behavior and appearance of the component.
   *
   * Note: If only a single allowedRetailerTenantTypes is specified, the manage-booking-info step will automatically
   * pass the value and hide the selection form-group. If there are multiple allowedRetailerTenantTypes, the user is
   * able to select from the allowed list.
   */
  retailerLookupOptions = input<Partial<RetailerLookupComponentOptions>>();

  @Output() bookingSubmitted = new EventEmitter<boolean>();

  spaceLimit: number = 2;
  spaceAvailabilityMap: Map<string, Map<string, SpaceAvailability>> = new Map();
  spaces: Space[] = [];
  availableSpaces: {
    fullyAvailable: Space[],
    partiallyAvailable: Space[]
  } = {fullyAvailable: [], partiallyAvailable: []};
  loadingSpaceData: boolean = false;

  leaseOccupancy: LeaseGridSpace[] = [];
  overlappingLeaseSpaces: LeaseGridSpace[] = [];
  dateConfigs: Map<string, Map<string, DateConfig>> = new Map();

  bookingConfig: BookingConfig = new BookingConfig();
  allIndustries: SelectableCategory[] = [];
  offeringId!: string;

  steps = [
    {
      label: 'Calendar',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'calendar'
    },
    {
      label: 'Term & Dates',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'dates_and_spaces'
    },
    {
      label: 'Rates & Charges',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'rates_and_charges'
    },
    {
      label: 'Tenant Information',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'tenant_info'
    },
    {
      label: 'Review & Save',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'review_and_submit',
    }
  ]

  currentStep = 0;

  constructor(private dateUtils: DateUtilsService,
              private bookingService: LeaseBookingsService,
              private industriesService: ManageIndustriesService,) {
  }

  ngOnInit() {
    this.offeringId = this.offering!.offeringUuid!;
    this.bookingConfig.spaceLimit = this.spaceLimit;
    this.bookingConfig.occupancyType = OccupancyType.SINGLE;

    this.loadingSpaceData = true;

    let focusDate = new Date();
    if (this.editType == 'edit' && this.lease) focusDate = new Date(this.lease!.leaseStartDate);

    this.loadSpaceAvailabilityMap(focusDate)
      .subscribe({
        next: () => {
          this.loadSpaces();
        },
        error: (err) => {
          console.log(err);
          this.loadingSpaceData = false;
        }
      })
    this.loadIndustries();
  }

  loadSpaceAvailabilityMap(focusDate: Date) {
    const fromDate = this.dateUtils.getFirstDayOfQuarter(focusDate);
    const toDate = this.dateUtils.getLastDayOfQuarter(focusDate);

    return this.bookingService.getSpaceAvailabilityMap(fromDate, toDate).pipe(
      tap(value => {
        this.addToSpaceMap(value);
      }),
      concatMap(() => this.loadLeaseOccupancy(fromDate, toDate)),
      map(() => void 0)
    );
  }

  loadSpaces() {
    this.bookingService.getSpacesForOffering().subscribe({
      next: value => {
        this.spaces = value;
        this.loadingSpaceData = false;
        this.spaces.forEach(ea => {
          ea.spaceLocation = this.offering!.label
        })
        if ((this.editType == 'edit' || this.editType == 'add') && this.lease) {
          this.retailerId = this.lease.retailerId;
          this.bookingConfig = {...this.generateBookingFromLease()};
          this.steps = this.steps.filter(f => f.code != 'tenant_info');
          this.updateAvailableSpaces(this.bookingConfig.selectedDates);
        } else if (!!this.retailerId) {
          this.bookingConfig.retailerId = this.retailerId;
          this.steps = this.steps.filter(f => f.code != 'tenant_info');
        }
      },
      error: err => {
        console.log(err)
      }
    })
  }

  loadLeaseOccupancy(startDate: string, endDate: string) {
    return this.bookingService.getLeaseOccupancy(startDate, endDate).pipe(
      tap(value => {
        this.addToOccupancyList(value);
      }),
      map(() => void 0)
    );
  }

  public loadMoreSpaceData(focusDate: Date) {
    const dateMonthKey = this.dateUtils.displayMonthAndYear(focusDate)!;
    if (!this.spaceAvailabilityMap.has(dateMonthKey)) {
      this.loadingSpaceData = true;
      this.loadSpaceAvailabilityMap(focusDate)
        .subscribe(() => this.loadingSpaceData = false);
    }
  }

  public loadMoreSpaceDataForDateRange(dateRange: { start: string, end: string }, dateSelection: string[]) {
    const fromDate = this.dateUtils.getFirstDayOfQuarter(new Date(dateRange.start));
    const toDate = this.dateUtils.getLastDayOfQuarter(new Date(dateRange.end));

    const requests = [];
    let focusDate = new Date(fromDate);

    while (focusDate <= new Date(toDate)) {
      const dateMonthKey = this.dateUtils.displayMonthAndYear(focusDate)!;
      if (!this.spaceAvailabilityMap.has(dateMonthKey)) {
        requests.push(this.loadSpaceAvailabilityMap(new Date(focusDate)));
      }
      focusDate = this.dateUtils.add(focusDate, 1, "quarters")!;
    }

    if (requests.length > 0) {
      this.loadingSpaceData = true;
      forkJoin(requests).subscribe({
        next: () => {
          this.updateAvailableSpaces(dateSelection);
          this.updateOverlappingLeaseSpaces(dateSelection);
          this.loadingSpaceData = false;
        },
        error: (err) => {
          console.log(err);
          this.loadingSpaceData = false;
        }
      });
    } else {
      this.updateAvailableSpaces(dateSelection);
      this.updateOverlappingLeaseSpaces(dateSelection);
    }
  }

  generateBookingFromLease(): BookingConfig {
    let booking = new BookingConfig();
    if (this.lease) {
      booking.occupancyType = this.lease.occupancyType!;

      if (this.editType == 'add') {
        booking.retailerId = this.lease.retailerId;
        booking.retailerCompanyName = this.lease.retailerCompanyName!;
        return booking;
      }

      if (this.lease.baseBookingConfig && this.lease.baseBookingConfig.selectedDates) {
        const selectedDates = this.lease.baseBookingConfig.selectedDates.map(m => this.dateUtils.displayShortDate(m)!);
        booking = {...this.lease.baseBookingConfig};
        booking.retailerId = this.lease.retailerId;
        booking.retailerCompanyName = this.lease.retailerCompanyName!;
        booking.preSelectedDates = selectedDates;
        booking.selectedDates = selectedDates;
      } else {
        const spaceIds = this.spaces
          .filter(f => this.lease!.spaceNames!.split(',').includes(f.spaceName))
          .map(f => f.id);

        const selectedDates = this.bookingService.generateDateRange(this.lease.leaseStartDate, this.lease.leaseEndDate);

        booking.retailerId = this.lease.retailerId;
        booking.retailerCompanyName = this.lease.retailerCompanyName!;
        booking.spaceIds = spaceIds;
        booking.startDate = this.lease.leaseStartDate;
        booking.endDate = this.lease.leaseEndDate;
        booking.selectedDates = selectedDates;
        booking.preSelectedDates = selectedDates;
        booking.spacePricingRateCategory = this.getCategoryFromRentalType(this.lease.rentalType);
        booking.patternType = this.getPatternTypeFromBookingConfig();
      }
    }
    booking.spaceLimit = this.spaceLimit;
    return booking;
  }

  getCategoryFromRentalType(rentalType: RentalPricingOption | null | undefined): SpacePricingRateCategory {
    const perSqm = [RentalPricingOption.FIXED_PER_SQM_PER_MONTH, RentalPricingOption.PRICING_MODEL, RentalPricingOption.TURNOVER_PERCENTAGE];
    const perPeriod = [RentalPricingOption.FIXED_PER_MONTH, RentalPricingOption.FIXED_FOR_PERIOD];

    if (rentalType) {
      if (perSqm.includes(rentalType)) return SpacePricingRateCategory.PER_SQM;
      if (perPeriod.includes(rentalType)) return SpacePricingRateCategory.PER_PERIOD;
    }
    return SpacePricingRateCategory.PER_PERIOD;
  }

  getPatternTypeFromBookingConfig(): PatternType {
    if (!this.lease || !this.lease!.baseBookingConfig) return PatternType.MONTHLY;
    return this.lease!.baseBookingConfig.patternType ?? PatternType.MONTHLY;
  }

  updateAvailableSpaces(dateSelection: string[]) {
    const available = this.bookingService.getAvailableSpacesForDateSelection(
      this.spaceAvailabilityMap,
      this.spaces,
      dateSelection);

    available.fullyAvailable = this.sortSpacesBySelected(available.fullyAvailable);
    available.partiallyAvailable = this.sortSpacesBySelected(available.partiallyAvailable);

    this.availableSpaces = available;
  }

  updateOverlappingLeaseSpaces(dateSelection: string[]) {
    this.overlappingLeaseSpaces = this.bookingService.getOccupiedLeasesForDateSelection(
      this.spaceAvailabilityMap,
      this.leaseOccupancy,
      dateSelection);
  }

  sortSpacesBySelected(spaces: Space[]) {
    return spaces.sort((a, b) => Number(this.isSpaceSelected(b)) - Number(this.isSpaceSelected(a)));
  }

  isSpaceSelected(space: Space) {
    return this.bookingConfig.spaceIds.includes(space.id);
  }

  addToSpaceMap(newMap: Map<string, Map<string, SpaceAvailability>>) {
    const currentMap = this.spaceAvailabilityMap;
    newMap.forEach((value, key) => {
      if (!currentMap.has(key)) {
        currentMap.set(key, value);
      }
    })
    this.spaceAvailabilityMap = currentMap;
    this.generateDateConfigs();
  }

  addToOccupancyList(newSpaces: OccupiedSpace[]) {
    const existingIds = this.leaseOccupancy.map(m => m.uuid);

    const spacesToAdd = newSpaces
      .flatMap((space) => space.leaseGridSpaceGroups)
      .flatMap((spaceGroup) => spaceGroup.leaseGridSpaces)
      .filter(f => !existingIds.includes(f.uuid));

    this.leaseOccupancy.push(...spacesToAdd);
  }

  // DATE CONFIGS

  public generateDateConfigs() {
    this.dateConfigs.clear();

    this.spaceAvailabilityMap.forEach((spaceMap, month) => {
      const monthMap = this.dateConfigs.get(month) || new Map();
      spaceMap.forEach((spaces, date) => {
        let label = '';
        let unavailable = false;

        if (spaces.availableSpaceIds.length === 0) {
          label = '';
          unavailable = true;
        } else {
          label = `${spaces.availableSpaceIds.length} spaces`;
          unavailable = false;
        }

        monthMap.set(date, {
          label,
          unavailable
        });
      })
      this.dateConfigs.set(month, monthMap);
    });
  }

  // INDUSTRIES

  loadIndustries() {
    this.industriesService.getAllSelectableOptionsForOffering().subscribe({
      next: value => this.allIndustries = value,
      error: err => console.log(err)
    })
  }

  // STEPPING

  isCurrentStep(code: string): boolean {
    const matchingStep = this.steps.find(f => f.code == code);
    if (matchingStep) {
      return matchingStep.code == this.steps[this.currentStep].code;
    }
    return false;
  }

  previousStep() {
    this.currentStep--;
  }

  nextStep() {
    this.steps[this.currentStep].control.setValue(true);

    this.currentStep++
    this.steps[this.currentStep].editable = true;
  }

  submitBooking(submitted: boolean = true) {
    this.steps.forEach(step => step.editable = false);
    this.bookingSubmitted.emit(submitted)
  }

}
